//services
//reducers
import { getTranfserAmount, getCompanyBankAccount } from 'state/reducers/balance.reducers';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import { getDomainSettings } from 'state/reducers/system_settings.reducers';

//components
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1, HeaderCaption1, HeaderPage4 } from 'theme/elements/Headers';

//mui

import { Box, Stack, Typography, useTheme } from '@mui/material';
//constants
import { BalanceDepositType, balanceTransStepsPrefix, digitalBalanceAddStepsPrefix } from 'constants/balance.constants';

import FormCreator from 'components/FormCreator';
import { fieldsGroupType, fieldType, invalidFields } from 'components/FormCreator/types';
import { useAppSelector } from 'hooks/redux.hooks';
import { digitalFieldsValidations } from '../../../shared/steps/step2/index';
import { getExistingBankAccounts, getAltshulerAccounts } from 'state/reducers/bank-account.reducers';
import React, { useMemo, useState } from 'react';
import { getCurrenciesObj, getBankList } from 'state/reducers/global.reducers';
import { getRawAmountInputValue } from 'utils';

export default function DepositAmountForm(props: {
	handleFormChange: any;
	invalidFields: invalidFields;
	availableAmount: number;
}) {
	const t = useAppSelector(selectTranslations);
	const [amount, setAmount] = useState<string>('');
	const transferAmount = useAppSelector(getTranfserAmount);
	const companyBankAccountId = useAppSelector(getCompanyBankAccount);
	const existBankAccounts = useAppSelector(getExistingBankAccounts);
	const altshulerAccounts = useAppSelector(getAltshulerAccounts);
	const currencies = useAppSelector(getCurrenciesObj);
	const bankList = useAppSelector(getBankList);
	const theme = useTheme();
	const [showErrorBox, setShowErrorBox] = useState(false);

	const balanceSettings = useAppSelector(getDomainSettings('balance'));
	const maxAmountToDigitalDeposit = balanceSettings?.maxAmountToDigitalDeposit || 0;

	// Find the selected bank account and matching Altshuler account
	const { selectedBankAccount, matchingAltshulerAccount } = useMemo(() => {
		const selectedBank = existBankAccounts.find((account) => account.id === companyBankAccountId);

		// Find the bank details from the bank list to get parentId
		const bankDetails = selectedBank ? bankList.find((bank) => bank.id === selectedBank.bankId) : null;

		const effectiveBankId = bankDetails?.parentBankId || selectedBank?.bankId;

		const matchingAltshular =
			selectedBank && effectiveBankId
				? altshulerAccounts.find((account) => account.bankId === effectiveBankId)
				: null;

		return {
			selectedBankAccount: selectedBank,
			matchingAltshulerAccount: matchingAltshular,
		};
	}, [existBankAccounts, altshulerAccounts, companyBankAccountId, bankList]);

	const localCurrencyId = useMemo(() => {
		return selectedBankAccount?.currencyId || (existBankAccounts?.length && existBankAccounts[0].currencyId);
	}, [selectedBankAccount, existBankAccounts]);

	// Use availableAmount from props and calculate display values
	const hasDigitalTransfers = props.availableAmount < maxAmountToDigitalDeposit;

	const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let { name, value } = event.target;
		value = getRawAmountInputValue(value);
		setAmount(value);
		props.handleFormChange({ name, value });
	};

	const currentAmount = useMemo(() => {
		return transferAmount || amount;
	}, [amount, transferAmount]);

	return (
		<Box component="form">
			<Stack marginTop="32px">
				<FormCard>
					<Stack spacing={4}>
						<Stack spacing={0}>
							<FormHeader1
								title={t(`${digitalBalanceAddStepsPrefix}.2.currency_approval.header.title`)}
								text={t(
									`${digitalBalanceAddStepsPrefix}.2.currency_approval.header.limit_info`
								).replace('{MaxAmount}', maxAmountToDigitalDeposit.toLocaleString())}
							/>

							<Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
								{hasDigitalTransfers
									? (
											t(
												`${digitalBalanceAddStepsPrefix}.2.currency_approval.header.available_amount`
											) || 'Available amount: {AvailableAmount}'
									  ).replace('{AvailableAmount}', props.availableAmount.toLocaleString())
									: t(`${digitalBalanceAddStepsPrefix}.2.currency_approval.header.amount_question`)}
							</Typography>
						</Stack>

						<FormCreator
							initialValidation={props.invalidFields}
							name="newBankAccount"
							handleChange={handleAmountChange}
							validations={digitalFieldsValidations}
							fieldsGroups={[
								{
									type: fieldsGroupType.col,
									spacing: 2,
									fields: [
										{
											type: fieldType.text,
											props: {
												disabled: true,
												name: 'currency',
												label: t(
													`${digitalBalanceAddStepsPrefix}.2.currency_approval.currency.text`
												),
												value: currencies[localCurrencyId].name,
											},
										},
										{
											type: fieldType.text,
											props: {
												required: true,
												formatAsAmount: true,
												name: 'amount',
												label: t(
													`${digitalBalanceAddStepsPrefix}.2.currency_approval.amount.text`
												),
												value: currentAmount,
												placeholder: t(
													`${balanceTransStepsPrefix}.2.currency_approval.amount.placeholder`
												),
											},
										},
									],
								},
							]}
						/>

						{matchingAltshulerAccount && (
							<Typography
								variant="body1"
								sx={{
									marginTop: 2,
									padding: 2,
									textAlign: 'center',
								}}
							>
								{(
									t(`${digitalBalanceAddStepsPrefix}.2.currency_approval.bank_account_info`) ||
									'Transfer to: {bankName} - {accountNumber}'
								)
									.replace('{bankName}', matchingAltshulerAccount.bankName?.bankName?.trim() || 'N/A')
									.replace('{accountNumber}', matchingAltshulerAccount.accountNumber || 'N/A')}
							</Typography>
						)}
					</Stack>
				</FormCard>
			</Stack>
		</Box>
	);
}
